import { useRef, useState, useEffect } from 'react';
import { Share2, Loader2 } from 'lucide-react';
import { toPng } from 'html-to-image';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { fetchUserGroups } from '@/lib/supabase-queries';
import BracketCapture from './BracketCapture';
import { BracketPicks } from '@/types/bracket';
import { BracketSlot } from '@/hooks/use-bracket';
import { trackEvent } from '@/lib/analytics';

interface BracketShareButtonProps {
  picks: BracketPicks;
  bracketSlots: BracketSlot[];
}

const BracketShareButton = ({ picks, bracketSlots }: BracketShareButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const captureRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const { user, displayName, avatarId } = useAuth();

  // Fetch invite code from user's first group on mount
  useEffect(() => {
    if (!user) return;
    fetchUserGroups(user.id).then(groups => {
      const code = groups?.[0]?.inviteCode ?? null;
      setInviteCode(code);
    }).catch(() => {
      // non-critical — share still works without invite code
    });
  }, [user]);

  const handleShare = async () => {
    if (!captureRef.current) return;

    setIsLoading(true);
    try {
      const dataUrl = await toPng(captureRef.current, {
        pixelRatio: 2,
        backgroundColor: 'hsl(0, 0%, 5%)',
      });

      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const file = new File([blob], 'bracket-run-my-bracket.png', { type: 'image/png' });

      // Step 1 — Web Share API (mobile)
      if (navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: 'BRACKET RUN',
          });
          trackEvent('bracket_shared');
          return; // success
        } catch (err) {
          if (err instanceof Error && err.name === 'AbortError') return; // user cancelled
          // Fall through to clipboard
        }
      }

      // Step 2 — Clipboard copy (desktop primary fallback)
      try {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob }),
        ]);
        trackEvent('bracket_shared');
        toast({
          title: 'הועתק!',
          description: 'תמונת הברקט הועתקה ללוח — הדבק בכל מקום!',
        });
        return;
      } catch {
        // Clipboard not supported — fall through to download
      }

      // Step 3 — Download (final fallback)
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'bracket-run-my-bracket.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      trackEvent('bracket_shared');
      toast({
        title: 'התמונה הורדה',
        description: 'שתף את הברקט שלך!',
      });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('Share failed:', error);
      toast({
        title: 'השיתוף נכשל',
        description: 'נסה שוב מאוחר יותר',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Only render when there are picks
  if (Object.keys(picks).length === 0) return null;

  return (
    <>
      <button
        onClick={handleShare}
        disabled={isLoading}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-border bg-card hover:bg-accent/10 transition-colors disabled:opacity-50"
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Share2 className="w-4 h-4" />
        )}
        שתף את הברקט שלי
      </button>

      <BracketCapture
        ref={captureRef}
        picks={picks}
        bracketSlots={bracketSlots}
        displayName={displayName || 'שחקן'}
        avatarId={avatarId}
        inviteCode={inviteCode}
      />
    </>
  );
};

export default BracketShareButton;
